
import React, { useState, useEffect } from 'react';
import { X, Info, Shield, BarChart3, Target, Settings } from 'lucide-react';
import { cookieManager, type ConsentData, type CookieCategory } from '../utils/cookieManager';
import Button from './Button';

interface CookiePreferencesProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (preferences: Omit<ConsentData, 'version' | 'timestamp' | 'expiryDate'>) => void;
}

const CategoryIcon = ({ categoryId }: { categoryId: string }) => {
  const icons = {
    essential: Shield,
    analytics: BarChart3,
    marketing: Target,
    preferences: Settings
  };
  
  const IconComponent = icons[categoryId as keyof typeof icons] || Info;
  return <IconComponent className="w-5 h-5" />;
};

const CookiePreferences: React.FC<CookiePreferencesProps> = ({ isOpen, onClose, onSave }) => {
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    preferences: false
  });
  
  const [activeTab, setActiveTab] = useState('settings');
  const categories = cookieManager.getCategories();

  useEffect(() => {
    if (isOpen) {
      const consent = cookieManager.getConsent();
      if (consent) {
        setPreferences({
          essential: consent.essential,
          analytics: consent.analytics,
          marketing: consent.marketing,
          preferences: consent.preferences
        });
      }
    }
  }, [isOpen]);

  const handleToggle = (categoryId: string) => {
    if (categoryId === 'essential') return; // Essential cookies cannot be disabled
    
    setPreferences(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId as keyof typeof prev]
    }));
  };

  const handleSave = () => {
    onSave(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setPreferences(allAccepted);
    onSave(allAccepted);
    onClose();
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    setPreferences(onlyEssential);
    onSave(onlyEssential);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-primary-brand">Cookie-Einstellungen</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors rounded-lg hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'settings' 
                  ? 'text-primary-brand border-b-2 border-primary-brand' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Einstellungen
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'details' 
                  ? 'text-primary-brand border-b-2 border-primary-brand' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Cookie-Details
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="prose prose-sm max-w-none text-gray-600">
                  <p>
                    Wir verwenden Cookies und ähnliche Technologien, um Ihnen die beste Erfahrung auf unserer Website zu bieten. 
                    Sie können Ihre Einstellungen jederzeit anpassen. Weitere Informationen finden Sie in unserer Datenschutzerklärung.
                  </p>
                </div>

                <div className="space-y-4">
                  {categories.map((category) => (
                    <div key={category.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="flex-shrink-0 mt-1">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              category.essential ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                            }`}>
                              <CategoryIcon categoryId={category.id} />
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">{category.name}</h3>
                            <p className="text-sm text-gray-600 mb-2">{category.description}</p>
                            <p className="text-xs text-gray-500">
                              {category.cookies.length} Cookie{category.cookies.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-4">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={preferences[category.id as keyof typeof preferences]}
                              onChange={() => handleToggle(category.id)}
                              disabled={category.essential}
                              className="sr-only peer"
                            />
                            <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-brand/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${
                              preferences[category.id as keyof typeof preferences] ? 'peer-checked:bg-primary-brand' : ''
                            } ${category.essential ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="space-y-6">
                {categories.map((category) => (
                  <div key={category.id} className="border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        category.essential ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                      }`}>
                        <CategoryIcon categoryId={category.id} />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                    </div>
                    
                    <div className="space-y-3">
                      {category.cookies.map((cookie, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Name:</span>
                              <p className="text-gray-600 font-mono">{cookie.name}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Anbieter:</span>
                              <p className="text-gray-600">{cookie.provider}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Laufzeit:</span>
                              <p className="text-gray-600">{cookie.duration}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Zweck:</span>
                              <p className="text-gray-600">{cookie.purpose}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button variant="outline" onClick={handleRejectAll}>
                Nur notwendige
              </Button>
              <Button variant="outline" onClick={handleAcceptAll}>
                Alle akzeptieren
              </Button>
              <Button variant="primary" onClick={handleSave}>
                Einstellungen speichern
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferences;
